/**
 * FilterSection Component
 * 라이더 출금제한 검색 필터 섹션
 */
import React, { useMemo } from 'react';
import SearchableSelect, { SelectOption } from '@/shared/components/forms/SearchableSelect';
import { ContractStatusOption } from '../types/riderAccounting.types';
import '@/shared/styles/search-form.css';

interface PartnerName {
    suppId: string;
    suppName: string;
}

interface FilterSectionProps {
    userType: string | null;
    searchPartnerId: string;
    searchRiderName: string;
    searchRiderId: string;
    searchContractStatus: string;
    searchWithdrawalStatus: string; // 출금상태 필터 추가
    partnerNames: PartnerName[] | null;
    contractStatusCodes: ContractStatusOption[];
    contractStatusLoading: boolean;
    loading: boolean;
    onPartnerIdChange: (value: string) => void;
    onRiderNameChange: (value: string) => void;
    onRiderIdChange: (value: string) => void;
    onContractStatusChange: (value: string) => void;
    onWithdrawalStatusChange: (value: string) => void; // 출금상태 필터 핸들러 추가
    onSearch: () => void;
}

const FilterSection: React.FC<FilterSectionProps> = ({
    userType,
    searchPartnerId,
    searchRiderName,
    searchRiderId,
    searchContractStatus,
    searchWithdrawalStatus, // 출금상태 필터 추가
    partnerNames,
    contractStatusCodes,
    contractStatusLoading,
    loading,
    onPartnerIdChange,
    onRiderNameChange,
    onRiderIdChange,
    onContractStatusChange,
    onWithdrawalStatusChange, // 출금상태 필터 핸들러 추가
    onSearch
}) => {
    // 협력사 옵션을 SelectOption 형식으로 변환
    const partnerOptions: SelectOption[] = useMemo(() => {
        if (!partnerNames) return [];
        return partnerNames.map((partner) => ({
            value: partner.suppId,
            label: partner.suppName
        }));
    }, [partnerNames]);

    // 본사 사용자 여부에 따라 그리드 클래스 결정
    const formClassName = userType === 'HEADQUARTERS'
        ? 'search-form rider-accounting-search-form-hq'
        : 'search-form rider-accounting-search-form';

    return (
        <div className="card search-card">
            <div className="card-body">
                <div className={formClassName}>
                    {/* 본사 사용자만 협력사 선택 필드 표시 */}
                    {userType === 'HEADQUARTERS' && (
                        <div className="form-group">
                            <div className="form-field">
                                <label className="form-label">
                                    <i className="fas fa-building" />
                                    협력사
                                </label>
                                <SearchableSelect
                                    options={partnerOptions}
                                    value={searchPartnerId}
                                    onChange={(value) => onPartnerIdChange(value)}
                                    placeholder="협력사 검색..."
                                    showAllOption={true}
                                    allOptionLabel="전체"
                                />
                            </div>
                        </div>
                    )}

                    {/* 라이더명 */}
                    <div className="form-group">
                        <div className="form-field">
                            <label className="form-label">
                                <i className="fas fa-user" />
                                라이더명
                            </label>
                            <input
                                type="text"
                                className="text-input"
                                value={searchRiderName}
                                onChange={(e) => onRiderNameChange(e.target.value)}
                                onKeyPress={(e) => e.key === 'Enter' && onSearch()}
                                placeholder="라이더명 입력..."
                            />
                        </div>
                    </div>

                    {/* 라이더ID */}
                    <div className="form-group">
                        <div className="form-field">
                            <label className="form-label">
                                <i className="fas fa-id-card" />
                                라이더ID
                            </label>
                            <input
                                type="text"
                                className="text-input"
                                value={searchRiderId}
                                onChange={(e) => onRiderIdChange(e.target.value)}
                                onKeyPress={(e) => e.key === 'Enter' && onSearch()}
                                placeholder="라이더ID 입력..."
                            />
                        </div>
                    </div>

                    {/* 계약상태 */}
                    <div className="form-group">
                        <div className="form-field">
                            <label className="form-label">
                                <i className="fas fa-file-contract" />
                                계약상태
                            </label>
                            <div className="status-select-wrapper">
                                <select
                                    className="status-select"
                                    value={searchContractStatus}
                                    onChange={(e) => onContractStatusChange(e.target.value)}
                                    disabled={contractStatusLoading}
                                >
                                    <option value="">전체</option>
                                    {contractStatusCodes && contractStatusCodes.length > 0 ? (
                                        contractStatusCodes.map((status) => (
                                            <option key={status.code} value={status.code}>
                                                {status.name}
                                            </option>
                                        ))
                                    ) : (
                                        !contractStatusLoading && <option disabled>계약상태 목록이 없습니다</option>
                                    )}
                                </select>
                            </div>
                        </div>
                    </div>

                    {/* 출금상태 */}
                    <div className="form-group">
                        <div className="form-field">
                            <label className="form-label">
                                <i className="fas fa-money-bill-wave" />
                                출금상태
                            </label>
                            <div className="status-select-wrapper">
                                <select
                                    className="status-select"
                                    value={searchWithdrawalStatus}
                                    onChange={(e) => onWithdrawalStatusChange(e.target.value)}
                                >
                                    <option value="">전체</option>
                                    <option value="N">출금허용</option>
                                    <option value="Y">출금제한</option>
                                </select>
                            </div>
                        </div>
                    </div>

                    {/* 버튼 그룹 */}
                    <div className="form-group button-group">
                        <button
                            className="btn btn-primary"
                            onClick={onSearch}
                            disabled={loading}
                        >
                            <i className="fas fa-search" />
                            조회
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default FilterSection;
